import {
  BlocksIcon,
  BookIcon,
  HistoryIcon,
  SettingsIcon,
  UserIcon,
} from 'lucide-react'

import { Link } from '../lib/Link'
import { useMe } from '../lib/auth-client'
import { usePathname } from '../lib/navigation'

const navigation = [
  {
    name: 'Planning',
    href: '/planning',
    icon: BlocksIcon,
    match: (p: string) => p === '/planning',
    public: false,
  },
  {
    name: 'Cookbook',
    href: '/recipes',
    icon: BookIcon,
    match: (p: string) => p === '/' || p.startsWith('/recipes'),
    public: true,
  },
  {
    name: 'History',
    href: '/history',
    icon: HistoryIcon,
    match: (p: string) => p === '/history',
    public: false,
  },
  {
    name: 'Settings',
    href: '/settings',
    icon: SettingsIcon,
    match: (p: string) => p === '/settings' || p.startsWith('/settings/'),
    public: false,
  },
]

function useNavItems() {
  const { data: me } = useMe()
  const signedIn = Boolean(me?.user?.tenantId)
  return navigation.filter((n) => n.public || signedIn)
}

function useSignedIn() {
  const { data: me } = useMe()
  return Boolean(me?.user && !me.user.isAnonymous)
}

function MobileNavItem({
  label,
  active,
  href,
  icon: Icon,
}: {
  label: string
  active?: boolean
  href: string
  icon: typeof BookIcon
}) {
  return (
    <Link
      href={href}
      aria-current={active ? 'page' : undefined}
      className={
        (active ? '' : ' text-white/70') +
        ' pt-3 pb-3 flex-1 min-w-0 overflow-hidden text-center py-2 flex flex-col sm:flex-row gap-1 items-center justify-center text-xs leading-tight'
      }
    >
      <div
        className={
          (active ? 'bg-green-600/50 ' : '') +
          'flex items-center justify-center gap-2 w-16 max-w-full sm:w-8 sm:mr-1 h-8 rounded-full'
        }
      >
        <Icon className="w-5 h-5" />
      </div>
      <span className="truncate max-w-full">{label}</span>
    </Link>
  )
}

export function MobileNavigation() {
  const pathname = usePathname()
  const items = useNavItems()
  const signedIn = useSignedIn()

  return (
    <nav
      className="pointer-coarse bg-green-950 text-white flex shadow-[0_0_20px_#00000033] sticky bottom-0 z-20 pb-[max(env(safe-area-inset-bottom,0px),var(--expo-sab,0px))]"
      aria-label="Hoofdnavigatie"
    >
      {items.map((nav) => (
        <MobileNavItem
          key={nav.href}
          label={nav.name}
          icon={nav.icon}
          href={nav.href}
          active={nav.match(pathname)}
        />
      ))}
      {!signedIn ? (
        <MobileNavItem
          label="Inloggen"
          icon={UserIcon}
          href="/login"
          active={pathname === '/login' || pathname.startsWith('/login/')}
        />
      ) : null}
    </nav>
  )
}

export function DesktopNavigation() {
  const pathname = usePathname()
  const items = useNavItems()
  const signedIn = useSignedIn()

  return (
    <header className="pointer-fine sticky top-0 z-20 px-3 pt-3 lg:px-5 lg:pt-4 pointer-events-none">
      <nav
        className="pointer-events-auto flex items-center gap-1.5 lg:gap-2 rounded-full bg-green-950/90 backdrop-blur-xl border border-white/10 shadow-[0_8px_32px_-8px_rgba(0,0,0,0.35)] px-2 py-1.5 lg:px-2.5 lg:py-2 w-fit mx-auto"
        aria-label="Hoofdnavigatie"
      >
        <div className="flex items-center gap-0.5 shrink-0">
          {items.map((nav) => {
            const active = nav.match(pathname)
            const Icon = nav.icon
            return (
              <Link
                key={nav.href}
                href={nav.href}
                aria-current={active ? 'page' : undefined}
                className={
                  'relative rounded-full px-4 lg:pr-5 py-2.5 text-sm tracking-wide ' +
                  'inline-flex items-center gap-2 ' +
                  'transition-colors duration-200 ' +
                  (active
                    ? 'bg-green-600/50 text-white'
                    : 'text-white/70 hover:bg-white/10 hover:text-white')
                }
              >
                <Icon className="w-4 h-4" />
                {nav.name}
              </Link>
            )
          })}
        </div>

        {!signedIn ? (
          <div className="flex items-center gap-1.5 shrink-0 ml-auto">
            <Link
              href="/login"
              aria-label="Inloggen"
              title="Inloggen"
              className="shrink-0 flex items-center justify-center rounded-full w-10 h-10 bg-white/12 hover:bg-white/20 active:bg-white/25 transition-colors duration-150"
            >
              <UserIcon className="w-5 h-5" strokeWidth={2} />
            </Link>
          </div>
        ) : null}
      </nav>
    </header>
  )
}
